import React from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'

import {
  TEXT_MEDIUM,
  TEXT_SMALL,
  METAL,
  SILVER,
  RED,
  APP_STATE,
} from '../../../constants'
import { SECONDARY_BUTTON } from '../../../constants/buttonThemes'
import Tooltip2 from '../Tooltip2'
import Button from '../Button'
import Text from '../Text'
import ErrorBoundary from '../ErrorBoundary'
import LoaderSmall from '../LoaderSmall'

const FlexEndWrapper = styled.div`
  display: flex;
  flex-wrap: wrap-reverse;
`

const SpaceBetweenWrapper = styled.div`
  display: flex;
  justify-content: space-between;
`

const SpaceBetweenAligned = styled(SpaceBetweenWrapper)`
  align-items: ${({ align }) => align};
`

const BottomWrapper = styled(SpaceBetweenAligned)`
  padding: 0 15px;
  align-items: center;
`

const FlexRow = styled.div`
  display: flex;
  justify-content: space-between;
  flex-direction: row;
  border-radius: 5px;
`

const HalfWrapper = styled.div`
  padding: 0 15px;
  border-radius: 5px;
  width: 50%;
`

const Spacer = styled.div`
  padding-top: 21px;
`

const AnchorPart = styled.div`
  padding: 0 15px;
  border-radius: 5px;
  height: 63px;
  display: flex;
  align-items: center;
  justify-content: center;
`

const StyledRow = styled.div`
  display: ${({ inline }) => (inline ? 'inline-block' : 'block')};
`

const SingleButtonWrapper = styled.div`
  margin: 0 2px;
`

const BigText = styled.div`
  font-size: 42px;
  line-height: 1;
  color: ${METAL};
`

const NoAuditDataWrapper = ({ content }) => {
  return <AnchorPart>{content}</AnchorPart>
}

const NonAuditableData = ({ auditData }) => {
  const { t } = useTranslation()
  return (
    <AnchorPart>
      <Text color={SILVER} bold={false}>
        {t('feed_audit_unavailable', { reason: auditData.reason })}
      </Text>
    </AnchorPart>
  )
}

const AuditableData = ({ auditData }) => {
  const { t } = useTranslation()
  return (
    <FlexRow>
      <HalfWrapper>
        <StyledRow>
          <Text size={TEXT_MEDIUM} color={METAL}>
            {t('feed_audit_score')}
          </Text>
        </StyledRow>
        {(() => {
          switch (auditData.progressStatus) {
            case null: //not yet audited
              return (
                <StyledRow>
                  <Text color={SILVER}>{t('feed_audit_unaudited')}</Text>
                </StyledRow>
              )
            case 0: //failed
              return (
                <StyledRow>
                  <Text color={RED}>{t('feed_audit_failed')}</Text>
                  <Spacer />
                </StyledRow>
              )
            case 1: //completed
              return (
                <StyledRow>
                  <BigText color={SILVER}>{auditData.score}%</BigText>
                </StyledRow>
              )
            case 2: //in progress
              return (
                <StyledRow>
                  <Text color={SILVER}>{t('feed_audit_in_progress')}</Text>
                </StyledRow>
              )
            default:
              return
          }
        })()}
      </HalfWrapper>
      <HalfWrapper>
        <StyledRow>
          <Text size={TEXT_MEDIUM} color={METAL}>
            {t('feed_audit_last_audited')}
          </Text>
        </StyledRow>
        <StyledRow>
          <Text color={SILVER}>
            {auditData.auditTimestamp
              ? t('dateValueInput', {
                  value: new Date(auditData.auditTimestamp),
                })
              : t('not_applicable')}
          </Text>
        </StyledRow>
      </HalfWrapper>
    </FlexRow>
  )
}

// 'auditData' is the individual card data, 'auditDataState' is the state of the request for whole page data
// So auditData.State references the state of the individual card
const CardAuditData = ({ auditData, auditDataState, auditButtons }) => {
  const { t } = useTranslation()

  return (
    <ErrorBoundary>
      {/* outgoing feed is auditable */}
      {auditData &&
        auditDataState == APP_STATE.DONE &&
        auditData.State == null &&
        auditData.auditable == true && <AuditableData auditData={auditData} />}

      {/* outgoing feed is not auditable and displays the reason why */}
      {auditData &&
        auditDataState == APP_STATE.DONE &&
        auditData.State == null &&
        auditData.auditable == false && (
          <NonAuditableData auditData={auditData} />
        )}

      {/* fetching or default initial state */}
      {(!auditDataState ||
        auditDataState == APP_STATE.FETCHING ||
        (auditData && auditData.State == APP_STATE.FETCHING)) && (
        <NoAuditDataWrapper
          content={
            <StyledRow>
              <LoaderSmall />
            </StyledRow>
          }
        />
      )}

      {/* errored state */}
      {(auditDataState == APP_STATE.ERROR ||
        (auditData && auditData.State == APP_STATE.ERROR)) && (
        <NoAuditDataWrapper
          content={
            <StyledRow>
              <Text color={RED} bold={false}>
                {t('feed_audit_fetch_error')}
              </Text>
            </StyledRow>
          }
        />
      )}

      {/* outgoing feed has just been created and doesn't exist in audit list */}
      {!auditData && auditDataState == APP_STATE.DONE && (
        <AnchorPart>
          <Text color={SILVER} bold={false}>
            {t('feed_audit_no_data')}
          </Text>
        </AnchorPart>
      )}

      <BottomWrapper align="flex-end">
        <div />
        {auditButtons && (
          <FlexEndWrapper>
            {auditButtons.map(
              ({
                name,
                disabled,
                tooltip,
                link,
                onClick,
                theme = SECONDARY_BUTTON,
              }) => (
                <SingleButtonWrapper key={name}>
                  <Tooltip2 title={t(tooltip)}>
                    <Button
                      dataId={`button-${name.replace(/\+/, '')}`}
                      fontSize={TEXT_SMALL}
                      onClick={onClick}
                      link={link}
                      theme={theme}
                      disabled={disabled}
                    >
                      {name}
                    </Button>
                  </Tooltip2>
                </SingleButtonWrapper>
              )
            )}
          </FlexEndWrapper>
        )}
      </BottomWrapper>

      <div />
    </ErrorBoundary>
  )
}

export default CardAuditData
